import { useMemo } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { TournamentActivity } from "@nestcoinco/hyper-api-gateway-api-client";

import { QUERY_KEYS } from "../constants";
import {
  handleGetResultActivities,
  handleGetUnviewedResultCount,
} from "../../api-clients/activitiesClient";
import { extractInfoFromInfiniteData, extractListRecords } from "../utils";
import { getMetadataValue } from "../../utils/helpers";
import useSubscription from "./use-subscription-status";

export function useInfiniteTournamentActivitiesQuery(params: {
  startDate: string;
  endDate: string;
  limit?: number;
  enabled?: boolean;
}) {
  const { startDate, endDate, limit = 10, enabled = true } = params;

  const query = useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.ACTIVITY.BASE,
      QUERY_KEYS.ACTIVITY.RESULT_ACTIVITIES,
      { startDate, endDate, limit },
    ],
    queryFn: ({ pageParam }) =>
      handleGetResultActivities({
        page: pageParam,
        limit,
        startDate,
        endDate,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled,
  });

  const pages = useMemo(
    () => extractInfoFromInfiniteData(query.data),
    [query.data]
  );

  const records = useMemo((): TournamentActivity[] => {
    const allRecords = extractListRecords(pages) as TournamentActivity[];
    return allRecords.filter((record) => {
      const subStatus = getMetadataValue(record, "subStatus");
      return subStatus !== "CANCELLED";
    });
  }, [pages]);

  return { records, query };
}

export function useUnviewedResultCountQuery() {
  const { isSubscribed } = useSubscription();

  return useQuery({
    queryKey: [QUERY_KEYS.ACTIVITY.BASE, QUERY_KEYS.ACTIVITY.UNVIEWED_COUNT],
    queryFn: handleGetUnviewedResultCount,
    enabled: isSubscribed === true,
  });
}
